import { useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useGlobal } from '@rcg/ui-react';
import { AIR_MAINTENANCE_STATUS } from '../operations/queries';
import { LDUser } from '../utils/ldUser';
import getBrand from '../utils/getBrand';
import { getSessionStorageItem } from '../operations/sessionStorage/utils';
import { trackError } from '../utils/analytics/browseTracking';
import {
  AIR_MAINTENANCE_MODE_FLAG,
  AIR_PRE_OUTAGE_BANNER_FLAG,
  APPLICATION_NAME,
  BOOKING_COUNTRY,
  MAINTENANCE_POLL_INTERVAL_MS,
  MAINTENANCE_STATUS_ERROR,
  MAINTENANCE_TRACKING_PAGE,
  MAINTENANCE_SUPPORT_PHONE_LD_FLAG,
} from '../utils/constants';
import {
  MAINTENANCE_DEFAULT_FLAGS,
  resolveMaintenanceMode,
  resolveSupportPhone,
} from '../utils/maintenance';

const DEFAULT_BOOKING_COUNTRY = { country: 'USA', officeID: 'mia' };

const readBookingCountry = () => {
  try {
    return getSessionStorageItem(BOOKING_COUNTRY) || DEFAULT_BOOKING_COUNTRY;
  } catch {
    return DEFAULT_BOOKING_COUNTRY;
  }
};

const useMaintenanceStatus = () => {
  const {
    data: { lang, country },
  } = useGlobal();

  const errorReported = useRef(false);
  const bookingCountry = readBookingCountry();

  const { loading, data, previousData } = useQuery(AIR_MAINTENANCE_STATUS, {
    variables: {
      ldUser: LDUser(),
      language: lang,
      country,
      clientApp: APPLICATION_NAME,
      application: APPLICATION_NAME,
      officeID: bookingCountry.officeID,
      bookingCountry: bookingCountry.country,
      brand: getBrand()?.ldBrand || 'ROYAL',
      defaultFlags: MAINTENANCE_DEFAULT_FLAGS,
    },
    fetchPolicy: 'no-cache',
    pollInterval: MAINTENANCE_POLL_INTERVAL_MS,
    onError: () => {
      if (errorReported.current) return;
      errorReported.current = true;
      trackError(MAINTENANCE_STATUS_ERROR, MAINTENANCE_TRACKING_PAGE);
    },
  });

  const flags = (data ?? previousData)?.ldFlags;

  return {
    loading: loading && !flags,
    mode: resolveMaintenanceMode(flags?.[AIR_MAINTENANCE_MODE_FLAG]),
    isBannerEnabled: flags?.[AIR_PRE_OUTAGE_BANNER_FLAG] === true,
    supportPhoneNumber: resolveSupportPhone(flags?.[MAINTENANCE_SUPPORT_PHONE_LD_FLAG]),
  };
};

export default useMaintenanceStatus;
